/**
 * Select Wrapper - Pure pass-through component
 * NO useState, NO useEffect, NO internal state
 * Parent owns value/onChange
 */
import React from 'react';
import {
  Select as MuiSelect,
  SelectProps,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  MenuItem,
  FormHelperText,
  styled,
} from '@mui/material';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface CustomSelectProps extends Omit<SelectProps<string>, 'onChange'> {
  options: SelectOption[];
  helperText?: string;
  onChange?: (event: SelectChangeEvent<string>, child: React.ReactNode) => void;
}

/**
 * Styled Select - filters notched prop to prevent React warning
 */
const StyledSelect = styled(MuiSelect<string>, {
  shouldForwardProp: (prop) => prop !== 'notched',
})({});

/**
 * Select - pure pass-through wrapper
 */
export const Select = React.forwardRef<HTMLDivElement, CustomSelectProps>(
  ({ options, label, helperText, error, fullWidth = true, value, onChange, ...rest }, ref) => {
    const labelId = label ? `select-${label}-label` : undefined;

    return (
      <FormControl fullWidth={fullWidth} error={error}>
        {label && <InputLabel id={labelId}>{label}</InputLabel>}
        <StyledSelect
          ref={ref}
          labelId={labelId}
          label={label}
          value={value as string}
          onChange={onChange}
          {...rest}
        >
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </MenuItem>
          ))}
        </StyledSelect>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

Select.displayName = 'Select';
export default Select;
